import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Card,
  Table,
  Dropdown,
  Spinner,
  InputGroup,
  Button,
  Row,
  Col
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faEye,
  faEdit,
  faEllipsisV,
  faSync,
  faPlusCircle
} from '@fortawesome/free-solid-svg-icons'
import { DebounceInput } from 'react-debounce-input'
import { Link, useParams, useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../Actions/AllAction'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import * as BookingEventsController from '../Controller/BookingEventsController'

const BookingList = (props) => {
  const { lng } = useParams()
  const history = useHistory()

  const [rows, setRows] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const suppressNextFetchRef = useRef(false)

  const [filters, setFilters] = useState({ limit: 10, offset: 0, search: '' })

  const performFetch = useCallback(
    async (effectiveFilters) => {
      try {
        props.loaderStateTrue()

        // Get store_id from user credentials or localStorage
        let storeId = null

        if (props.userEntryType?.user_type === 'staff') {
          if (localStorage.getItem('store_id') == null) {
            localStorage.setItem('store_id', props.userEntryType.branch_id)
          }
          storeId = localStorage.getItem('store_id')
        } else {
          // For admin users, get store_id from user credentials or use default
          storeId =
            props.userCredentials?.store_id || localStorage.getItem('store_id')
        }

        // Add store_id to filters
        const filtersWithStore = {
          ...effectiveFilters,
          store_id: storeId
        }

        console.log('Fetching bookings with filters:', filtersWithStore)
        const response = await BookingEventsController.getBookingsApi(
          filtersWithStore
        )
        if (response.success) {
          setRows(response.data || [])
          setTotalItems(response.total || 0)
        } else {
          setRows([])
          setTotalItems(0)
        }
      } catch (err) {
        setRows([])
        setTotalItems(0)
      } finally {
        props.loaderStateFalse()
      }
    },
    [props.userEntryType, props.userCredentials]
  )

  const handleRefresh = () => {
    fetchRows()
  }

  const fetchRows = useCallback(async () => {
    await performFetch(filters)
  }, [filters, performFetch])

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false
      return
    }
    fetchRows()
  }, [fetchRows])

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setCurrentPage(1)
    setFilters((prev) => ({ ...prev, limit: newLimit, offset: 0 }))
  }

  const handleSearch = (e) => {
    const raw = e.target.value || ''
    const clean = raw.replace(/\s+/g, '')
    setSearchTerm(clean)
    setCurrentPage(1)
    const next = {
      ...filters,
      search: clean.length >= 2 ? clean : '',
      offset: 0
    }
    suppressNextFetchRef.current = true
    setFilters(next)
    if (clean.length >= 2 || clean.length === 0) performFetch(next)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({ ...prev, offset: (page - 1) * prev.limit }))
  }

  const dateTime = (value) => Utility.getSafeTimeFormat(value, '-')

  //======= add action permission ====//
  // Check multiple permissions at once
  const bookingPermissions = Utility.hasMultipleActionPermissions(
    'bookings_and_calendar',
    'bookings_events',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Check if any actions are available for display in the table
  const hamburgerTableActions = bookingPermissions.view || bookingPermissions.edit || bookingPermissions.delete;

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Booking & Events</h4>
        <div>
          {bookingPermissions.create && (
            <Button
              variant='primary'
              size='sm'
              as={Link}
              to={`/${lng}/booking-events/add`}
            >
              New Booking or Event
            </Button>
          )}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>

          <Row className="mb-0 d-flex justify-content-end">
            <Col md={3} className='mb-3'>
              <InputGroup className='input-group input-group-sm'>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <DebounceInput
                  minLength={1}
                  debounceTimeout={500}
                  className='form-control'
                  placeholder='Search...'
                  value={searchTerm || ''}
                  onChange={(e) => handleSearch(e)}
                  onKeyDown={(e) => {
                    if (e.key === ' ') e.preventDefault()
                  }}
                />
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={props.loaderState}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={props.loaderState}
                    />
                  </Button>
                </div>
              </InputGroup>
            </Col>
          </Row>

          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th>Event Type</th>
                  <th>Event Code</th>
                  <th style={{ minWidth: '110px' }}>Start</th>
                  <th style={{ minWidth: '110px' }}>End</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Contact No</th>
                  <th>Timestamp</th>
                  <th>Created By</th>
                  <th>Store</th>
                  {hamburgerTableActions && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {props.loaderState ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? '11' : '10'} className='text-center py-4'>
                      {/* <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>Loading bookings...</span> */}
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((r) => (
                    <tr 
                      key={r.id}
                      onDoubleClick={() => {
                        if (r?.slack) {
                          history.push(`/${lng}/booking-events/booking/view/${r.slack}`);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{r.event_type}</td>
                      <td>{r.event_code}</td>
                      <td>
                        <div>{dateTime(r.start_date)?.split(' at')[0]}</div>
                        <div className='text-muted small'>{dateTime(r.start_date)?.split(' at')[1] ? `at ${dateTime(r.start_date).split(' at')[1]}` : ''}</div>
                      </td>
                      <td>
                        <div>{dateTime(r.end_date)?.split(' at')[0]}</div>
                        <div className='text-muted small'>{dateTime(r.end_date)?.split(' at')[1] ? `at ${dateTime(r.end_date).split(' at')[1]}` : ''}</div>
                      </td>
                      <td>{r.name}</td>
                      <td>{r.email}</td>
                      <td>{r.phone}</td>
                      <td style={{ minWidth: '170px' }}>
                        <div style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <FontAwesomeIcon icon={faPlusCircle} style={{ fontSize: '0.65rem', color: '#28a745', flexShrink: 0 }} />
                          <span>{dateTime(r.created_at)}</span>
                        </div>
                        {r.created_at !== r.updated_at && (
                          <div style={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.65rem', color: '#ffc107', flexShrink: 0 }} />
                            <span>{dateTime(r.updated_at)}</span>
                          </div>
                        )}
                      </td>
                      <td>{r.created_by}</td>
                      <td>{r.store?.name || '-'}</td>
                      {hamburgerTableActions && (
                        <td>
                          <Dropdown align='end'>
                            <Dropdown.Toggle
                              variant='light'
                              size='sm'
                              id={`dropdown-${r.id}`}
                              className='btn-icon no-caret'
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {/* {bookingPermissions?.view && ( */}
                              <Dropdown.Item
                                as={Link}
                                to={`/${lng}/booking-events/booking/view/${r.slack}`}
                              >
                                <FontAwesomeIcon icon={faEye} className='me-2' />
                                View
                              </Dropdown.Item>
                              {/* )} */}
                              {bookingPermissions?.edit && (
                                <Dropdown.Item
                                  as={Link}
                                  to={`/${lng}/booking-events/booking/edit/${r.slack}`}
                                >
                                  <FontAwesomeIcon icon={faEdit} className='me-2' />
                                  Edit
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={hamburgerTableActions ? '11' : '10'} className='text-center py-4 nosearchResult'>
                      {searchTerm
                        ? 'No bookings found for your search.'
                        : 'No bookings found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
        </Card.Body>
      </Card>
    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType,
  loaderState: globalState.mainReducerData.loaderState
})

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse, handleActiveLink })(BookingList)
